import Dexie, { type EntityTable } from "dexie";
import type { InfoStoreReader, SideEffect } from ".";
import { getFieldResponsesId } from "../aggregate";

type Db = Dexie & {
  fieldResponses: EntityTable<
    {
      proposalId: string;
      isCurrent: boolean;
      fieldResponsesId: string;
      fieldResponses: Record<string, string>;
    },
    "fieldResponsesId"
  >;
};

function getDb(): Db {
  const db = new Dexie("ClientInfoStore") as Db;

  db.version(1).stores({
    fieldResponses:
      "fieldResponsesId, [proposalId+fieldResponsesId], proposalId",
  });

  return db;
}

export default class ClientInfoStore implements InfoStoreReader {
  db: Db;

  // In-flight cold-client warmer (see prefetchAllCurrentIfCold). Never
  // rejects — failures resolve to {} and the on-demand fetch covers the rest.
  #prefetchedAll: Promise<Record<string, Record<string, string>>> | null = null;

  // True once the cold check observed an EMPTY fieldResponses table and
  // nothing has been written since. Lets ensureFieldResponses skip a bulkGet
  // that can only miss — on the iPad's Lockdown-Mode storage stand-in, a
  // few hundred guaranteed-miss reads cost whole seconds of a round load.
  // Cleared before any write, so a later ensure sees real cache contents.
  #dbKnownEmpty = false;

  // Map<proposalId, Map<version, fieldResponses>>
  #cache: Map<string, Map<string, Record<string, string>>> = new Map();

  // Map<proposalId, latestVersion>
  #latestVersion: Map<string, string> = new Map();

  #subscriptionsByProposalId: Map<
    string,
    Map<string, (fieldResponses: Record<string, string>) => void>
  > = new Map();
  #allFieldResponsesSubscriptions: Map<
    string,
    (fieldResponses: Record<string, Record<string, string>>) => void
  > = new Map();

  constructor(
    private fetchFieldResponses?: (
      fieldResponsesIds: string[],
    ) => Promise<Record<string, Record<string, string>>>,
    db?: Db,
    // Optional offline signal (passed in so the engine stays decoupled from the
    // client network hook). When it reports offline, a failed field-response
    // fetch is tolerated (serve cached); online, the failure is rethrown.
    private isOffline?: () => boolean,
  ) {
    this.db = db ?? getDb();
  }

  /**
   * Cold-client warmer. On a browser with nothing persisted yet (first visit,
   * new device, cleared storage) the round load's ensureFieldResponses ends in
   * one big network fetch — late, because the version ids it needs come from
   * the aggregate, which itself takes most of the load to arrive. But the
   * SERVER can derive the current ids from the round alone, so a cold client
   * may start that fetch immediately (`fetchAll` = getFieldResponses with no
   * ids). ensureFieldResponses consumes the result and only network-fetches
   * versions it didn't cover. On a warm client this is a no-op after one
   * indexed count, so returning users never re-download proposal text they
   * already have.
   */
  async prefetchAllCurrentIfCold(
    fetchAll: () => Promise<Record<string, Record<string, string>>>,
  ): Promise<void> {
    if (this.#prefetchedAll != null) return;
    try {
      if ((await this.db.fieldResponses.count()) > 0) return;
      this.#dbKnownEmpty = true;
    } catch {
      return; // unreadable local db — let the normal load path handle it
    }
    this.#prefetchedAll = fetchAll().catch((e: unknown) => {
      console.error(
        "field-response prefetch failed; falling back to the on-demand fetch",
        e,
      );
      return {};
    });
  }

  readNewestFieldResponses(proposalId: string): Record<string, string> | null {
    const version = this.#latestVersion.get(proposalId);
    if (!version) return null;
    return this.#cache.get(proposalId)?.get(version) ?? null;
  }

  readFieldResponsesFromCache(
    proposalId: string,
    version: string,
  ): Record<string, string> | null {
    return this.#cache.get(proposalId)?.get(version) ?? null;
  }

  getAllFieldResponses(): Record<string, Record<string, string>> {
    const result: Record<string, Record<string, string>> = {};
    for (const [proposalId, version] of this.#latestVersion.entries()) {
      const responses = this.#cache.get(proposalId)?.get(version);
      if (responses) {
        result[proposalId] = responses;
      }
    }
    return result;
  }

  async readFieldResponsesAtVersion(
    proposalId: string,
    version: string,
  ): Promise<Record<string, string> | null> {
    // 1. Check Memory
    const cached = this.#cache.get(proposalId)?.get(version);
    if (cached) return cached;

    // 2. Check DB
    const fieldResponses = await this.db.fieldResponses.get({
      fieldResponsesId: version,
    });

    if (fieldResponses) {
      this.#addToCache(proposalId, version, fieldResponses.fieldResponses);
      return fieldResponses.fieldResponses;
    }

    // 3. Fetch
    if (this.fetchFieldResponses) {
      try {
        const fetched = await this.fetchFieldResponses([version]);
        if (!fetched[version]) return null;

        // Cache write only — best-effort, so a broken IndexedDB doesn't
        // discard text we just fetched successfully.
        try {
          this.#dbKnownEmpty = false;
          await this.db.fieldResponses.put({
            proposalId,
            isCurrent: false,
            fieldResponsesId: version,
            fieldResponses: fetched[version],
          });
        } catch (e) {
          console.error(
            `Failed to cache field responses for version ${version}; serving fetched copy`,
            e,
          );
        }

        this.#addToCache(proposalId, version, fetched[version]);
        return fetched[version];
      } catch (e) {
        console.error(
          `Failed to fetch field responses for version ${version}`,
          e,
        );
        return null;
      }
    }

    return null;
  }

  #addToCache(
    proposalId: string,
    version: string,
    fieldResponses: Record<string, string>,
  ) {
    if (!this.#cache.has(proposalId)) {
      this.#cache.set(proposalId, new Map());
    }
    this.#cache.get(proposalId)!.set(version, fieldResponses);
  }

  subscribeToFieldResponses(
    proposalId: string,
    callback: (fieldResponses: Record<string, string>) => void,
  ): () => void {
    const randomId = Math.random().toString(36).substring(2, 15);

    if (!this.#subscriptionsByProposalId.has(proposalId)) {
      this.#subscriptionsByProposalId.set(proposalId, new Map());
    }
    this.#subscriptionsByProposalId.get(proposalId)!.set(randomId, callback);

    return () => {
      this.#subscriptionsByProposalId.get(proposalId)!.delete(randomId);
    };
  }

  subscribeToAllFieldResponses(
    callback: (fieldResponses: Record<string, Record<string, string>>) => void,
  ): () => void {
    const randomId = Math.random().toString(36).substring(2, 15);
    this.#allFieldResponsesSubscriptions.set(randomId, callback);

    return () => {
      this.#allFieldResponsesSubscriptions.delete(randomId);
    };
  }

  async applySideEffects(sideEffects: SideEffect[]): Promise<void> {
    const changedProposalIds = new Set<string>();

    for (const sideEffect of sideEffects) {
      if (sideEffect.type === "WRITE_FIELD_RESPONSES") {
        const version = getFieldResponsesId(sideEffect.fieldResponses);
        this.#addToCache(
          sideEffect.proposalId,
          version,
          sideEffect.fieldResponses,
        );
        this.#latestVersion.set(sideEffect.proposalId, version);
        changedProposalIds.add(sideEffect.proposalId);
      }
      // Ignore other (round / round member) side effects
    }

    // Batch Notify
    if (changedProposalIds.size > 0) {
      this.#batchNotifySubscribers(changedProposalIds);
    }

    this.#dbKnownEmpty = false;
    await this.db.fieldResponses.bulkPut(
      Array.from(changedProposalIds).map((proposalId) => {
        const version = this.#latestVersion.get(proposalId)!;
        const fieldResponses = this.#cache.get(proposalId)!.get(version)!;
        return {
          proposalId,
          isCurrent: true,
          fieldResponsesId: version,
          fieldResponses,
        };
      }),
    );
  }

  /**
   * Drop every cached field response, in memory and on disk. Called on an
   * account/schema change so the previous account's proposal text does not
   * linger next to a freshly-wiped round store (under Lockdown Mode that text
   * is persisted to a native file). Field responses are re-fetched on demand,
   * so this is always safe.
   */
  async clearFieldResponseCache(): Promise<void> {
    this.#cache.clear();
    this.#latestVersion.clear();
    await this.db.fieldResponses.clear();
  }

  #batchNotifySubscribers(changedProposalIds: Set<string>) {
    // 1. Notify individual proposal subscribers
    for (const proposalId of changedProposalIds) {
      const version = this.#latestVersion.get(proposalId);
      if (!version) continue;
      const responses = this.#cache.get(proposalId)?.get(version);
      if (!responses) continue;

      const subscriptions = this.#subscriptionsByProposalId.get(proposalId);
      if (subscriptions) {
        for (const callback of subscriptions.values()) {
          callback(responses);
        }
      }
    }

    // 2. Notify "all" subscribers ONCE
    const allFieldResponses = this.getAllFieldResponses();
    for (const callback of this.#allFieldResponsesSubscriptions.values()) {
      callback(allFieldResponses);
    }
  }

  async ensureFieldResponses(
    requiredVersions: { proposalId: string; version: string }[],
  ): Promise<void> {
    const missingFromMemory = requiredVersions.filter(
      (rv) => !this.#cache.get(rv.proposalId)?.has(rv.version),
    );

    if (missingFromMemory.length === 0) return;

    const versions = missingFromMemory.map((rv) => rv.version);
    let dbItems: ({ fieldResponses: Record<string, string> } | undefined)[];
    if (this.#dbKnownEmpty) {
      // Every keyed get would miss; skip straight to the fetch below.
      dbItems = versions.map(() => undefined);
    } else {
      // A failed cache read is a cache miss, not a load failure: fall through
      // to the network fetch below rather than failing the round load over a
      // broken IndexedDB (backend lost / corrupt profile — both seen in
      // production).
      try {
        dbItems = await this.db.fieldResponses.bulkGet(versions);
      } catch (e) {
        console.error(
          `Failed to read ${versions.length} field-response version(s) from cache; fetching from server`,
          e,
        );
        dbItems = versions.map(() => undefined);
      }
    }

    const missingFromDb: { proposalId: string; version: string }[] = [];
    const changedProposalIds = new Set<string>();

    for (let i = 0; i < missingFromMemory.length; i++) {
      const rv = missingFromMemory[i];
      const dbItem = dbItems[i];
      if (rv == null) {
        continue;
      }
      if (dbItem) {
        // Found in DB
        this.#addToCache(rv.proposalId, rv.version, dbItem.fieldResponses);
        this.#latestVersion.set(rv.proposalId, rv.version);
        changedProposalIds.add(rv.proposalId);
      } else {
        missingFromDb.push(rv);
      }
    }

    if (missingFromDb.length > 0) {
      if (!this.fetchFieldResponses) {
        console.warn(
          "fetchFieldResponses not provided, cannot fetch missing field responses",
        );
      } else {
        // Fetch missing from server
        const uniqueVersions = [
          ...new Set(missingFromDb.map((m) => m.version)),
        ];

        if (uniqueVersions.length > 0) {
          // A cold-client prefetch (prefetchAllCurrentIfCold) may already hold
          // most of these versions — consume it first and network-fetch only
          // the remainder. Bounded wait: if the prefetch is unexpectedly slow,
          // fall through to the normal fetch rather than gate the round load
          // on it (the prefetch settling later is harmless).
          const fromPrefetch: Record<string, Record<string, string>> = {};
          let versionsToFetch = uniqueVersions;
          if (this.#prefetchedAll != null) {
            const prefetched = await Promise.race([
              this.#prefetchedAll,
              new Promise<Record<string, Record<string, string>>>((resolve) =>
                setTimeout(() => resolve({}), 3_000),
              ),
            ]);
            versionsToFetch = uniqueVersions.filter((version) => {
              const hit = prefetched[version];
              if (hit) fromPrefetch[version] = hit;
              return hit == null;
            });
          }

          // A fetch failure while OFFLINE must not abort the whole round load:
          // serve whatever is already in memory/Dexie and leave the unfetchable
          // versions absent (the UI renders a placeholder). But an ONLINE
          // failure (server/permission/serialization error) is a real fault —
          // swallowing it would let funders evaluate applications with proposal
          // text silently missing, so rethrow it. When no offline signal is
          // wired (desktop/tests), treat as online and rethrow (original behavior).
          let fetched: Record<string, Record<string, string>> = fromPrefetch;
          try {
            if (versionsToFetch.length > 0) {
              fetched = {
                ...fromPrefetch,
                ...(await this.fetchFieldResponses(versionsToFetch)),
              };
            }
          } catch (e) {
            if (!(this.isOffline?.() ?? false)) {
              throw e;
            }
            console.error(
              `Failed to fetch ${versionsToFetch.length} field-response version(s) while offline; serving cached only`,
              e,
            );
          }

          for (const [version, fieldResponses] of Object.entries(fetched)) {
            const proposals = missingFromDb.filter(
              (m) => m.version === version,
            );
            for (const p of proposals) {
              this.#addToCache(p.proposalId, version, fieldResponses);
              this.#latestVersion.set(p.proposalId, version);
              changedProposalIds.add(p.proposalId);
            }
          }

          // Persisting the fetched text is a cache write — the in-memory
          // copies above are what render, so the round load does not wait on
          // it (a few hundred rows on the iPad's Lockdown-Mode stand-in cost
          // ~1.7s). Best-effort for the same reason: a full disk or dead
          // IndexedDB backend cannot fail a round load whose data is in hand.
          // Ordered before ensure resolves: Dexie serializes same-table
          // transactions, so the round-load gc that follows still runs after
          // this write.
          this.#dbKnownEmpty = false;
          void this.db.fieldResponses
            .bulkPut(
              missingFromDb
                .map(({ proposalId, version }) => {
                  const fieldResponses = fetched[version];
                  if (!fieldResponses) return null;
                  return {
                    proposalId,
                    isCurrent: true,
                    fieldResponsesId: version,
                    fieldResponses,
                  };
                })
                .filter((m) => m !== null),
            )
            .catch((e: unknown) => {
              console.error(
                "Failed to persist fetched field responses to cache; continuing with in-memory copies",
                e,
              );
            });
        }
      }
    }

    if (changedProposalIds.size > 0) {
      this.#batchNotifySubscribers(changedProposalIds);
    }
  }

  async gc(keepFieldResponsesIds: Set<string>): Promise<void> {
    // Clean memory
    for (const [proposalId, versionMap] of this.#cache.entries()) {
      for (const version of versionMap.keys()) {
        if (!keepFieldResponsesIds.has(version)) {
          versionMap.delete(version);
        }
      }
      if (versionMap.size === 0) {
        this.#cache.delete(proposalId);
        this.#latestVersion.delete(proposalId);
      } else {
        // If current latest is deleted, we might want to update #latestVersion?
        // For now, assume keepFieldResponsesIds includes current stuff.
        const currentLatest = this.#latestVersion.get(proposalId);
        if (currentLatest && !keepFieldResponsesIds.has(currentLatest)) {
          this.#latestVersion.delete(proposalId);
        }
      }
    }

    // Cache housekeeping only — best-effort, so a broken IndexedDB can't fail
    // the round load that runs this (unswept entries are retried next load).
    try {
      await this.db.fieldResponses
        .filter((item) => !keepFieldResponsesIds.has(item.fieldResponsesId))
        .delete();
    } catch (e) {
      console.error("Failed to gc field-response cache; continuing", e);
    }
  }
}
